// This component represent each individual table field dropped inside dropzone
// Each card has some aggregate values and option to select different aggregate and/or timeGrain values

import { useEffect, useMemo, useRef, useState } from 'react';
import '../Card.css';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  editChartPropItem,
  revertAxes,
  sortAxes,
  enableOverrideForUIDAction,
  createChartAxesForUID,
  setMeasureAxisFields,
  reorderParameterizedCharts,
  updateAxesQueryParam,
  deleteItemInChartProp,
  removeChartAxesForUID,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import { Divider, Menu, MenuItem, Tooltip, styled } from '@mui/material';
import { useDrag, useDrop } from 'react-dnd';
import { Card as CardProps } from '../ChartAxesInterfaces';
import {
  editChartPropItemForDm,
  revertAxesForDm,
  sortAxesForDm,
} from '../../../redux/DynamicMeasures/DynamicMeasuresActions';
import Logger from '../../../Logger';
import { CardOption } from '../CardOption';
import WindowFunction from '../CardComponents/WindowFunction/WindowFunction';

import RenameFunction from '../CardComponents/RenameFunction';
import {
  arraysMatch,
  checkIfMissingColumn,
  findNewDisplayName,
  getRoundingDigits,
} from '../../CommonFunctions/CommonFunctions';
import { TooltipProps } from '@mui/material/Tooltip';
import { setDisplayName } from '../setDisplayName';
import ChartsInfo from '../ChartsInfo2';
import { AxisType, messages } from '../../CommonFunctions/aliases';
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import SnackBar from '../../SnackBar';
import WindowFunctionButton from './WindowFunctionButton';
import OverrideFunction from './OverrideFunction';
import WindowFunctionMenuItem from './WindowFunctionMenuItem';
import RollUpButton from './RollUpButton';
import AggregationDisplay from './AggregationDisplay';
import AggregationMenuItem from './AggregationMenuItem';
import TimeGrainMenuItem from './TimeGrainMenuItem';
import OverrideMenuItem from './OverrideMenuItem';
import MenuOptions from './MenuOptions';
import {
  callQueryAPI,
  MeasureValue,
  removeColumnSecurityCard,
  removeMeasureMappingColumn,
  removeRowSecurityCard,
  SetModifyOptions,
  toggleMeasureAsLine,
  updateChartData,
  updateFormatOption,
  updateUserSelection,
  updateUserSelectionColumn,
  toggleMeasureAsBar,
  updateAxisOptions,
  deleteSortConfiguration,
  addOrEditSimplecardConditionalFormat,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../../redux';
import { reorderChartData } from '../chartDataFunctions/reorderChartData';
import { setPrefix } from '../SetPrefix';
import Warning from '@mui/icons-material/WarningAmber';
import { deleteFilterFromParameter } from '../../../redux/Parameters/ParametersAction';
import { SortConfiguration } from '../../../redux/ChartPoperties/ChartControlsInterface';
import SourceDetails from './SourceDetails';
import { handleCheckDisableField } from '../../ChartOptions/Sort/reusableFunctions';
import IgnoreReportFilterMenuItem from './IgnoreReportFilterOption';
import WarningIcon from '@mui/icons-material/WarningAmber';

const CustomTooltip = styled(
  ({ className, ...props }: TooltipProps & { isShowWarning?: boolean }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(({ isShowWarning }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: 'white',
    color: isShowWarning ? '#850000ff' : '#2bb9bb',
    fontSize: '13px',
    border: `1px solid ${isShowWarning ? '#850000ff' : '#2bb9bb'}`,
  },
  [`& .MuiTooltip-arrow`]: {
    color: 'white',
    '&::before': {
      width: '10px', // Adjust arrow width
      height: '14px', // Adjust arrow height
      border: `1px solid ${isShowWarning ? '#850000ff' : '#2bb9bb'}`,
    },
  },
}));

type DragItem = {
  uId?: string;
  bIndex?: number;
  dragFrom?: string;
  [key: string]: any;
};

const Card = ({
  field,
  bIndex,
  itemIndex,
  propKey,
  axisTitle,
  uID,
  snackBarOptions,
}: CardProps) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  let uidOrders = useRef<string[] | null>(null);
  const {
    chartControls,
    chartProp,
    dynamicMeasureState,
    tabTileProps,
    parameters,
    axisOptions,
    sortConfigurations,
    formatOptions,
    savedCalcs,
  } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProp: state.chartProperties,
      dynamicMeasureState: state.dynamicMeasuresState,
      parameters: state.parameters,
      axisOptions: state.chartControls.properties[propKey].axisOptions,
      savedCalcs: state.calculations.savedCalculations,
      sortConfigurations: state.chartControls.properties[propKey].sortConfigurations || [],
      formatOptions: state.chartControls.properties[propKey].formatOptions,
    };
  });

  const currentOperator =
    chartControls?.properties[propKey]?.columnSecurity?.currentOperator?.toLowerCase();

  field.dataType = field?.dataType?.toLowerCase();

  const [showSourceDetails, setShowSourceDetails] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const selectedDs = useMemo(() => {
    return chartProp.properties[propKey]?.selectedDs;
  }, [chartProp.properties[propKey]?.selectedDs?.id, chartProp.properties[propKey]?.oldSelectedDs]);

  let chartType =
    chartProp.properties[`${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`].chartType;
  const checkedMeasure =
    chartControls.properties[propKey]?.formatOptions?.tooltipFormats?.checkedMeasure || [];
  const barLineSettings = chartControls.properties[propKey]?.barLineSettings;

  const savedMeasureMappings =
    chartControls?.properties[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[1]?.mapping ?? [];
  const measureMappingWarning =
    chartControls?.properties[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[1] &&
    [AxisType.Measure, AxisType.X, AxisType.Y, 'X', 'Y'].includes(axisTitle) &&
    !savedMeasureMappings.find((mapping: any) => mapping[field.uId || '']);

  const getAllMeasureFieldsGroupBy = () => {
    const axes = chartProp.properties[propKey]?.chartAxes || [];

    const targetAxes = [AxisType.Measure, AxisType.X, AxisType.Y];

    const grouped: Record<string, any[]> = {};

    targetAxes.forEach((axisName) => {
      const axis = axes.find((a: any) => a.name === axisName);
      grouped[axisName] = axis?.fields ?? [];
    });

    return grouped;
  };

  const getAllMeasureFields = (): any[] => {
    const axes = chartProp.properties[propKey]?.chartAxes || [];

    const targetAxes = [AxisType.Measure, AxisType.X, AxisType.Y];
    return axes
      .filter((axis: any) => targetAxes.includes(axis.name))
      .flatMap((axis: any) => axis.fields ?? []);
  };

  const originalIndex =
    chartType === 'richText'
      ? dynamicMeasureState.dynamicMeasureProps?.[dynamicMeasureState.selectedTabId]?.[
          dynamicMeasureState.selectedTileId
        ]?.[
          `${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`
        ].chartAxes[bIndex].fields.findIndex((item: any) => item?.uId === field?.uId)
      : chartProp.properties[propKey].chartAxes[bIndex].fields.findIndex(
          (item: any) => item?.uId === field?.uId,
        );

  /**
   * This function removes a measure from bar or line list if a measure is removed from axis.
   * Also, for bar chart if one and only bar is removed then this function is changing first measure in available measures as the bar.
   * Similarly, for line/area chart if one and only line is removed then this function is changing first measure in available measure as the line.
   */
  function removeItemFromBarLine(from: string, deletedMeasureUid: string) {
    let newLineMeasuresList = [...barLineSettings?.lineMeasures];
    if (from === 'barChart') {
      if (
        getAllMeasureFields()?.length - barLineSettings?.lineMeasures.length === 1 &&
        !barLineSettings?.lineMeasures.includes(deletedMeasureUid)
      ) {
        newLineMeasuresList.splice(0, 1);
      } else if (barLineSettings?.lineMeasures.includes(deletedMeasureUid)) {
        let measureIndex = barLineSettings?.lineMeasures.findIndex(
          (uId: string) => uId === deletedMeasureUid,
        );
        newLineMeasuresList.splice(measureIndex, 1);
      }
      dispatch(toggleMeasureAsLine(propKey, newLineMeasuresList));
    }

    let newBarMeasuresList = [...barLineSettings?.barMeasures];
    if (from === 'lineChart') {
      if (
        getAllMeasureFields()?.length - barLineSettings?.barMeasures.length === 1 &&
        !barLineSettings?.barMeasures.includes(deletedMeasureUid)
      ) {
        newBarMeasuresList.splice(0, 1);
      } else if (barLineSettings?.barMeasures.includes(deletedMeasureUid)) {
        let measureIndex = barLineSettings?.barMeasures.findIndex(
          (uId: string) => uId === deletedMeasureUid,
        );
        newBarMeasuresList.splice(measureIndex, 1);
      }
      dispatch(toggleMeasureAsBar(propKey, newBarMeasuresList));
    }
  }

  const deleteItem = async () => {
    /**
     * uID means its a card in override axis
     */
    let filterDeleted = false;
    const axesToPick = ['Row', 'Column', 'Dimension'];
    const dimensions = (chartProp.properties[propKey]?.[currentChartAxesName] || [])
      .filter((axis: any) => axesToPick.includes(axis.name))
      .flatMap((axis: any) => axis.fields || []);
    /**if current area is row or column or dimension then we ahev to  subtract 1 as we are deleting current element */
    const newDimensionLength = axesToPick.includes(axisTitle)
      ? dimensions.length - 1
      : dimensions.length;
    if (newDimensionLength === 0) {
      const chartFields = chartProp.properties[propKey]?.[currentChartAxesName]?.[0]?.fields || [];
      const toBeDeletedFilterIds: string[] = [];
      chartFields.forEach((fieldItem: any) => {
        if (!['Aggregate Condition', 'Top N'].includes(fieldItem.fieldtypeoption)) return;
        toBeDeletedFilterIds.push(fieldItem.uId);
        const parameterId = fieldItem.parameterId;
        if (!parameterId) return;

        parameters.savedParameters.forEach((param: any) => {
          if (
            param.parameterId === parameterId &&
            Array.isArray(param.chartFilter) &&
            param.chartFilter.length > 0
          ) {
            // Find all filters for this chartAxes
            const chartFilters = param.chartFilter.filter(
              (cf: any) => cf.currentChartAxesName === currentChartAxesName,
            );
            if (chartFilters.length > 0) {
              const updatedChartFilters = param.chartFilter.map((cf: any) => {
                if (cf.currentChartAxesName === currentChartAxesName && cf.itemIndex > itemIndex) {
                  return { ...cf, itemIndex: cf.itemIndex - 1 };
                }
                return cf;
              });
              dispatch(reorderParameterizedCharts(propKey, param.parameterId, updatedChartFilters));
            }
          }
        });
      });
      toBeDeletedFilterIds.forEach((filterId) => {
        const filterIndex = chartFields.findIndex((f: any) => f.uId === filterId);
        if (filterIndex === -1) return;

        const filter = chartFields[filterIndex];
        filterDeleted = true;
        dispatch(updateAxesQueryParam(propKey, 0, filterIndex, filter, currentChartAxesName));
        dispatch(deleteItemInChartProp(propKey, 0, filterIndex, currentChartAxesName));
        dispatch(deleteFilterFromParameter(propKey, filter.uId as string, currentChartAxesName));
      });
    }
    dispatch(SetModifyOptions(propKey, uID ? 'filter_override_open' : 'axis', null));

    let showSnackBar = false;
    //----------------when we delete an item and current chart has only one axis then set measure value as empty string ----------------//
    const measureAxis = ChartsInfo[chartProp.properties[propKey].chartType].dropZones.find(
      (dropzone: any) =>
        dropzone.name === 'Measure' || dropzone.name === 'X Axis' || dropzone.name === 'Y Axis',
    );
    if (measureAxis?.allowedNumbers === 1) {
      dispatch(MeasureValue(propKey, ''));
    }
    let deletedMeasureUid = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.uId;

    // Close override panel if the deleted field is currently in override mode
    if (chartProp.properties[propKey]?.enableOverrideForUID === deletedMeasureUid) {
      dispatch(enableOverrideForUIDAction(propKey, ''));
      dispatch(removeChartAxesForUID(propKey, deletedMeasureUid));
      dispatch(SetModifyOptions(propKey, 'filter_override_close', null));
    }

    //-------------------update checked measure if deleted item uId is present in checked measure ----------//
    if (checkedMeasure.includes(deletedMeasureUid)) {
      let measureIndex = checkedMeasure.findIndex((uId: string) => uId === deletedMeasureUid);
      checkedMeasure.splice(measureIndex, 1);
      dispatch(updateFormatOption(propKey, 'tooltipFormats', 'checkedMeasure', checkedMeasure));
    }
    //---delete measure formats for that measure
    ['labelFormats', 'tooltipFormats'].forEach((formatName: any) => {
      const measureFormats = formatOptions[formatName].measureFormats;
      if (measureFormats && measureFormats[deletedMeasureUid]) {
        const { [deletedMeasureUid]: _, ...remainingFormats } = measureFormats;
        dispatch(updateFormatOption(propKey, formatName, 'measureFormats', remainingFormats));
      }
    });
    //-----------------------------------update lineMeasures and barMeasures list when a measure is removed ---------//
    removeItemFromBarLine('barChart', deletedMeasureUid);
    removeItemFromBarLine('lineChart', deletedMeasureUid);
    //----------------------------------------------------------------------------------------------//
    //---------Remove deleted measure uid from measuresList of a axis series -------------------------//
    ['xAxis', 'yAxis'].forEach((axis) => {
      const seriesList: string[] = Object.keys(axisOptions[axis]);
      seriesList.forEach((series) => {
        let seriesMeasureList = axisOptions[axis][series].measuresList;
        if (seriesMeasureList.includes(deletedMeasureUid)) {
          let updatedList = seriesMeasureList.filter((id: string) => id !== deletedMeasureUid);
          dispatch(updateAxisOptions(propKey, axis, 'measuresList', updatedList, series));
        }
      });
    });
    //-------------------------------------------------------------------------------------------------//
    //---------------------delete sort configuration of removed field ---------------------------------//
    const deletedFieldConfigIndex = sortConfigurations.findIndex(
      (config: SortConfiguration) => config.fieldId === deletedMeasureUid,
    );
    if (deletedFieldConfigIndex !== -1) {
      dispatch(deleteSortConfiguration(propKey, deletedFieldConfigIndex));
    }

    //---------------------clean up invalid sort configurations after field removal ---------------------------------//
    // This handles cases where removing this field makes other sort configurations invalid
    if (sortConfigurations.length > 0) {
      // Create a updated state after the current field is removed
      const updatedChartAxes = chartProp.properties[propKey].chartAxes.map(
        (axis: any, axisIndex: number) => {
          if (axisIndex === bIndex) {
            return {
              ...axis,
              fields: axis.fields.filter((_: any, fieldIndex: number) => fieldIndex !== itemIndex),
            };
          }
          return axis;
        },
      );

      // Create temporary configs excluding the one we just deleted
      let tempConfigs = sortConfigurations.filter(
        (config: SortConfiguration) => config.fieldId !== deletedMeasureUid,
      );

      // Check remaining configs in reverse order
      for (let i = tempConfigs.length - 1; i >= 0; i--) {
        const config = tempConfigs[i];

        // Check if this config would be disabled after the field removal
        const isDisabled = handleCheckDisableField(
          config.fieldId,
          propKey,
          {
            properties: {
              [propKey]: {
                ...chartProp.properties[propKey],
                chartAxes: updatedChartAxes, // Sending the updated chart axes
              },
            },
          },
          tempConfigs,
        );

        if (config.sortOrder !== '' && isDisabled) {
          // Find the actual index in the original sortConfigurations array
          const actualIndex = sortConfigurations.findIndex(
            (c: SortConfiguration) => c.id === config.id,
          );
          if (actualIndex !== -1) {
            dispatch(deleteSortConfiguration(propKey, actualIndex));
            tempConfigs = tempConfigs.filter((_: SortConfiguration, idx: number) => idx !== i);
          }
        }
      }
    }

    //-------------------------------------------------------------------------------------------------//
    const allMeasureFields = getAllMeasureFields();

    if (chartType === 'richText') {
      dispatch(
        editChartPropItemForDm('delete', {
          propKey,
          binIndex: bIndex,
          itemIndex,
        }),
      );
    } else {
      if (chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.override) {
        dispatch(enableOverrideForUIDAction(propKey, ''));
      }
      if ([AxisType.Measure, AxisType.X, AxisType.Y].includes(axisTitle)) {
        const savedParameters = parameters.savedParameters;
        savedParameters.forEach((parameter: any) => {
          if (parameter.chartFilter) {
            const chartFilter = parameter.chartFilter.filter(
              (filter: any) => !filter.currentChartAxesName.includes(uID),
            );
            if (
              parameter.chartFilter.find((filter: any) => filter.currentChartAxesName.includes(uID))
            ) {
              dispatch(reorderParameterizedCharts(propKey, parameter.parameterId, chartFilter));
            }
          }
        });

        dispatch(
          editChartPropItem('delete', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            currentChartAxesName,
          }),
        );
        if (chartProp.properties[propKey].chartType === 'simplecard') {
          dispatch(addOrEditSimplecardConditionalFormat(propKey, []));
        }
        dispatch(
          removeMeasureMappingColumn(
            propKey,
            currentOperator,
            chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.uId,
          ),
        );
        if (allMeasureFields.length === 0) {
          const id =
            chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
              chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator?.toLowerCase()
            ]?.filters[0].source;
          dispatch(
            updateUserSelectionColumn(
              propKey,
              chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase(),
              [],
            ),
          );
          dispatch(
            removeColumnSecurityCard(
              propKey,
              id,
              chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator?.toLowerCase(),
            ),
          );
        }
        dispatch(callQueryAPI(propKey, true));
        return;
      }

      if (
        allMeasureFields.length === 0 ||
        allMeasureFields.filter((field: any) => field.windowfn).length === 0
      ) {
        dispatch(
          editChartPropItem('delete', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            currentChartAxesName,
          }),
        );
        dispatch(removeMeasureMappingColumn(propKey, currentOperator, itemIndex));
      } else {
        showSnackBar = true;
        if (!uID) {
          // let allowedNumbers = ChartsInfo[chartType].dropZones[bIndex].allowedNumbers;
          // const newMeasureFields = allMeasureFields.map((field: any) => {
          //   if (!field.windowfn) return field;
          //   else
          //     return {
          //       ...field,
          //       windowfn: null,
          //       displayname: findNewDisplayName(
          //         chartProp.properties[propKey].chartAxes,
          //         { ...field, displayname: fieldName(field) }, // for getting name without window fn
          //         allowedNumbers,
          //       ),
          //     };
          // });

          const measures = getAllMeasureFieldsGroupBy();
          for (const [axisName, fields] of Object.entries(measures)) {
            let dropzoneInfo = ChartsInfo[chartType].dropZones.find(
              (dropzone: any) => dropzone.name === axisName,
            );
            if (dropzoneInfo?.allowedNumbers) {
              let newNotAllowednames: string[] = [];
              const modifiedMeasures = fields.map((field: any) => {
                if (!field.windowfn) return field;
                else {
                  if (field.isTextRenamed) return { ...field, windowfn: null };
                  const newFieldData = setDisplayName(
                    setPrefix({ ...field, windowfn: null }, axisName, chartType),
                    axisName,
                    chartType,
                  );
                  newFieldData.displayname = findNewDisplayName(
                    chartProp.properties[propKey].chartAxes,
                    newFieldData,
                    chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats,
                    newNotAllowednames,
                  );
                  newNotAllowednames.push(newFieldData.displayname);
                  return newFieldData;
                }
              });
              dispatch(setMeasureAxisFields(propKey, modifiedMeasures, axisName));
            }
          }

          // dispatch(setMeasureAxisFields(propKey, newMeasureFields));
        }
        dispatch(
          editChartPropItem('delete', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            currentChartAxesName,
          }),
        );
        dispatch(removeMeasureMappingColumn(propKey, currentOperator, itemIndex));
      }
    }

    if (bIndex !== 0) {
      const dimensionFieldsName: string[] = [
        AxisType.Row,
        AxisType.Column,
        AxisType.Dimension,
        AxisType.SplitBy,
        AxisType.Location,
      ];
      const dimensionAxisFields = chartProp.properties[propKey].chartAxes
        .filter((axis: any) => {
          return dimensionFieldsName.includes(axis.name);
        })
        .flatMap((axis: any) => axis.fields);
      if (dimensionAxisFields.length === 0) {
        const id =
          chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
            chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase()
          ]?.filters[0].source;
        dispatch(
          updateUserSelection(
            propKey,
            chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase(),
            [],
          ),
        );
        dispatch(
          removeRowSecurityCard(
            propKey,
            id,
            chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase(),
          ),
        );
      }
      dispatch(callQueryAPI(propKey, true));
    }

    if (showSnackBar && snackBarOptions)
      snackBarOptions({ show: true, message: messages.playbook.WindowFunctionRemoved });

    if (filterDeleted && snackBarOptions)
      snackBarOptions({ show: true, message: messages.playbook.RemovingFilter });
  };

  let currentChartAxesName = uID ? 'chartAxes_' + uID : 'chartAxes';

  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [anchorElment, setAnchorElement] = useState<any | null>(null);

  //Window function open/close and enable/disable
  const [windowFunction, setWindowFunction] = useState<boolean>(false);
  const [overrideFn, setOverrideFn] = useState<boolean>(false);
  const [disable, setDisable] = useState<{
    windowFn: boolean;
    override: boolean;
  }>({
    windowFn: false,
    override: false,
  });
  const [renameFunction, setRenameFunction] = useState<boolean>(false);
  // const [renameText, setRenameText] = useState<string>("");

  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (chartControls.properties[propKey].callQueryAPI === false) {
      const reorderedData = reorderChartData(
        chartControls.properties[propKey].chartData,
        chartProp.properties[propKey].chartAxes,
      );
      dispatch(updateChartData(propKey, reorderedData));
    }
  }, [chartProp.properties[propKey].chartAxes]);

  const open: boolean = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);

    setShowTooltip(false);
  };

  const handleClose = (closeFrom: any, queryParam?: any) => {
    dispatch(SetModifyOptions(propKey, 'axis', null));
    setAnchorEl(null);
    setShowOptions(false);
    let updateDispName = true;

    if (closeFrom === 'agg' || closeFrom === 'timeGrain') {
      var field2 = JSON.parse(JSON.stringify(field));

      if (closeFrom === 'agg') {
        Logger('info', 'Aggregate Choice selected', queryParam);
        if (field2.agg === queryParam) {
          updateDispName = false;
        }
        field2.agg = queryParam;
      } else if (closeFrom === 'timeGrain') {
        if (field2.timeGrain === queryParam) {
          updateDispName = false;
        }
        field2.timeGrain = queryParam;
        if (
          chartType !== 'table' &&
          chartType !== 'crossTab' &&
          (axisTitle === 'Measure' || axisTitle === 'X' || axisTitle === 'Y') &&
          queryParam === 'date'
        ) {
          field2.agg = 'count';
        }
      }
      const roundingDigits = getRoundingDigits(field2);
      const currentAxisName = chartProp.properties[propKey].chartAxes[bIndex].name;
      //---Skip measureFormats update for Split By and Distribution axes---//
      if (currentAxisName !== 'Distribution') {
        ['labelFormats', 'tooltipFormats'].map((formatName) => {
          const formatObject = chartControls.properties[propKey].formatOptions[formatName];
          dispatch(
            updateFormatOption(propKey, formatName, 'measureFormats', {
              ...formatObject.measureFormats,
              [field2.uId]: {
                ...formatObject.measureFormats[field2.uId],
                roundingDigits: roundingDigits,
              },
            }),
          );
        });
      }
      if (chartType === 'richText') {
        Logger('info', 'queryparam');
        dispatch(
          editChartPropItemForDm('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: field2,
          }),
        );
        dispatch(callQueryAPI(propKey, true));
      } else {
        if (updateDispName) {
          field2 = setDisplayName(
            field2,
            chartProp.properties[propKey].chartAxes[bIndex].name,
            chartProp.properties[propKey].chartType,
          );
          /**
           * this new displayname  is inside condition beacuse  for aggregated calcualtion that returns date/timestamp if we change its aggregation that modifies displayname everytime this condition check is to prevent that
           */
          if (!field2.isAggregated) {
            field2.displayname = findNewDisplayName(
              chartProp.properties[propKey].chartAxes,
              field2,
            );
            field2.isTextRenamed = false;
          }
          dispatch(
            editChartPropItem('updateQuery', {
              propKey,
              binIndex: bIndex,
              itemIndex,
              item: field2,
              currentChartAxesName,
            }),
          );
          dispatch(callQueryAPI(propKey, true));
        }
      }
      //------------update checked measure if field is changed to date type(start)-------------//
      const axisName = chartProp.properties[propKey].chartAxes[bIndex].name;
      if (
        closeFrom === 'timeGrain' &&
        axisName !== 'Measure' &&
        axisName !== 'X Axis' &&
        axisName !== 'Y Axis' &&
        axisName !== 'Distribution'
      ) {
        if (field2.timeGrain === 'date') {
          checkedMeasure.push(field2.uId);
        } else {
          if (checkedMeasure.includes(field2.uId)) {
            let measureIndex = checkedMeasure.findIndex((uId: string) => uId === field.uId);
            checkedMeasure.splice(measureIndex, 1);
          }
        }
        dispatch(updateFormatOption(propKey, 'tooltipFormats', 'checkedMeasure', checkedMeasure));
      }
      //-------------------------------------(End)---------------------------------------------//
    }
  };

  const handleOverrideOnClick = (event: any, optionName: any) => {
    dispatch(SetModifyOptions(propKey, 'filter_override_open', null));
    if (optionName.id === 'override') {
      setAnchorEl(null);
      setOverrideFn(true);
      setShowTooltip(false);

      let oldChartAxes = JSON.parse(JSON.stringify(chartProp.properties[propKey].chartAxes || []));
      let filters = oldChartAxes[0].fields;
      if (filters && filters.length > 0) {
        oldChartAxes[0].fields = oldChartAxes[0].fields.filter(
          (filterItem: any) =>
            !['Aggregate Condition', 'Top N'].includes(filterItem.fieldtypeoption) &&
            !filterItem.isAggregated,
        );
      }
      let overRideAxes = JSON.parse(
        JSON.stringify(
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].override || [],
        ),
      );

      dispatch(
        createChartAxesForUID(
          propKey,
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].uId,
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].override
            ? overRideAxes
            : oldChartAxes,
        ),
      );

      dispatch(
        enableOverrideForUIDAction(
          propKey,
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].uId,
        ),
      );
      // to reset the disableReportFilterForOverride
      // let updatedField = JSON.parse(
      //   JSON.stringify(chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex])
      // );

      // updatedField.disableReportFilterForOverride = true;

      // dispatch(
      //   editChartPropItem("updateQuery", {
      //     propKey,
      //     binIndex: bIndex,
      //     itemIndex,
      //     item: updatedField,
      //     currentChartAxesName,
      //   })
      // );
    } else {
      let field2 = JSON.parse(JSON.stringify(field));
      setAnchorEl(null);
      field2.disableReportFilterForOverride = !field2.disableReportFilterForOverride;
      dispatch(
        editChartPropItem('updateQuery', {
          propKey,
          binIndex: bIndex,
          itemIndex,
          item: field2,
          currentChartAxesName,
        }),
      );
    }
  };

  const handleWindowFunctionOnClick = () => {
    dispatch(SetModifyOptions(propKey, 'axis', null));
    setShowTooltip(false);
    setWindowFunction(true);
    handleClose('clickOutside');
  };

  const handleRollup = () => {
    dispatch(SetModifyOptions(propKey, 'axis', null));
    let field2 = JSON.parse(JSON.stringify(field));
    setAnchorEl(null);
    setShowTooltip(false);
    field2.rollupDepth = !field2.rollupDepth;
    field2.isManual = field2.rollupDepth;
    dispatch(
      editChartPropItem('updateQuery', {
        propKey,
        binIndex: bIndex,
        itemIndex,
        item: field2,
        currentChartAxesName,
      }),
    );

    let _charAxesFields = chartProp.properties[propKey][currentChartAxesName][bIndex].fields;

    if (field2.rollupDepth) {
      if (
        ['crossTab', 'heatmap', 'boxPlot', 'bubbleMap'].includes(
          chartProp.properties[propKey].chartType,
        )
      ) {
        let chartAxesOneFields = chartProp.properties[propKey][currentChartAxesName][1].fields;
        let chartAxesTwoFields = chartProp.properties[propKey][currentChartAxesName][2].fields;

        if (bIndex === 1) {
          if (chartAxesOneFields?.length > 0) {
            for (let i = 0; i < chartAxesOneFields?.length; i++) {
              if (itemIndex !== i) {
                updateFieldRollupProperty(1, i, false);
              }
            }
          }

          if (chartAxesTwoFields?.length > 0) {
            for (let i = 0; i < chartAxesTwoFields?.length; i++) {
              updateFieldRollupProperty(2, i, false);
            }
          }
        } else {
          if (chartAxesOneFields?.length > 0) {
            for (let i = 0; i < chartAxesOneFields?.length; i++) {
              updateFieldRollupProperty(1, i, false);
            }
          }

          if (chartAxesTwoFields?.length > 0) {
            for (let i = 0; i < chartAxesTwoFields?.length; i++) {
              if (itemIndex !== i) {
                updateFieldRollupProperty(2, i, false);
              }
            }
          }
        }
      } else {
        for (let i = 0; i < _charAxesFields?.length; i++) {
          if (itemIndex !== i) {
            let _field = _charAxesFields[i];

            if (_field) {
              let _tempField = JSON.parse(JSON.stringify(_field));

              if (_tempField) {
                _tempField.rollupDepth = false;
                dispatch(
                  editChartPropItem('updateQuery', {
                    propKey,
                    binIndex: bIndex,
                    itemIndex: i,
                    item: _tempField,
                    currentChartAxesName,
                  }),
                );
              }
            }
          }
        }
      }
    } else {
      if (
        ['crossTab', 'heatmap', 'boxPlot', 'bubbleMap'].includes(
          chartProp.properties[propKey].chartType,
        )
      ) {
        if (chartProp.properties[propKey][currentChartAxesName][2].fields?.length > 0) {
          updateFieldRollupProperty(
            2,
            chartProp.properties[propKey][currentChartAxesName][2].fields?.length - 1,
            true,
          );
        } else {
          updateFieldRollupProperty(
            1,
            chartProp.properties[propKey][currentChartAxesName][1].fields?.length - 1,
            true,
          );
        }
      } else {
        let _field = _charAxesFields[_charAxesFields?.length - 1];

        if (_field) {
          let _tempField = JSON.parse(JSON.stringify(_field));

          if (_tempField) {
            _tempField.rollupDepth = true;
            dispatch(
              editChartPropItem('updateQuery', {
                propKey,
                binIndex: bIndex,
                itemIndex: _charAxesFields?.length - 1,
                item: _tempField,
                currentChartAxesName,
              }),
            );
          }
        }
      }
    }

    function updateFieldRollupProperty(binIndex: number, index: number, enable: boolean) {
      let _field = chartProp.properties[propKey][currentChartAxesName][binIndex].fields[index];

      if (_field) {
        let _tempField = JSON.parse(JSON.stringify(_field));

        if (_tempField) {
          _tempField.rollupDepth = enable;
          dispatch(
            editChartPropItem('updateQuery', {
              propKey,
              binIndex: binIndex,
              itemIndex: index,
              item: _tempField,
              currentChartAxesName,
            }),
          );
        }
      }
    }
  };

  let menuStyle = { fontSize: '12px', padding: '2px 1.5rem' };
  let menuSelectedStyle = {
    fontSize: '12px',
    padding: '2px 1.5rem',
    backgroundColor: 'rgba(175, 153, 219,0.3)',
    '&:hover': {
      backgroundColor: 'rgba(175, 153, 219, 0.5)',
    },
  };

  // Properties and behaviour when a card is dragged
  const [, drag] = useDrag({
    item: () => {
      if (!field) return;
      const isOverride = Boolean(uID);
      if (!uidOrders.current) {
        const fields = chartProp.properties[propKey]?.chartAxes?.[bIndex]?.fields;
        if (fields && fields.length > 0) {
          uidOrders.current = fields.map((field: any) => field.uId);
        }
      }
      return {
        ...field,
        bIndex,
        originalIndex,
        dragFrom: isOverride ? `${axisTitle}-override` : axisTitle,
        override: null,
        windowfn: null,
        overrideUId: uID,
      };
    },
    type: 'card',

    end: (dropResult, monitor) => {
      const item = monitor.getItem();
      if (!item || !item?.uId || item?.bIndex === undefined) {
        return;
      }
      const { uId, bIndex, originalIndex } = item;

      const didDrop = monitor.didDrop();
      if (!didDrop) {
        if (chartType === 'richText') {
          dispatch(revertAxesForDm(propKey, bIndex, uId as string, originalIndex));
        } else {
          dispatch(revertAxes(propKey, bIndex, uId as string, originalIndex, currentChartAxesName));
        }
      } else {
        if (axisTitle !== AxisType.Measure || axisTitle !== AxisType.ChartFilter) {
          if (uidOrders.current) {
            let newOrders = [];
            const fields = chartProp.properties[propKey]?.chartAxes?.[bIndex]?.fields;
            if (fields && fields.length > 0) {
              newOrders = fields.map((field: any) => field.uId);
            }
            if (arraysMatch(uidOrders.current, newOrders)) {
              uidOrders.current = null;
              return;
            }
          }
          uidOrders.current = null;
          let showSnackBar = false;
          const allMeasureFields = getAllMeasureFields();
          if (
            allMeasureFields.length === 0 ||
            allMeasureFields.filter((field: any) => field.windowfn).length === 0
          )
            return;
          else {
            showSnackBar = true;
            if (!uID) {
              const measures = getAllMeasureFieldsGroupBy();
              for (const [axisName, fields] of Object.entries(measures)) {
                let dropzoneInfo = ChartsInfo[chartType].dropZones.find(
                  (dropzone: any) => dropzone.name === axisName,
                );
                if (dropzoneInfo?.allowedNumbers) {
                  let newNotAllowednames: string[] = [];
                  const modifiedMeasures = fields.map((field: any) => {
                    if (!field.windowfn) return field;
                    else {
                      if (field.isTextRenamed) return { ...field, windowfn: null };
                      const newFieldData = setDisplayName(
                        setPrefix({ ...field, windowfn: null }, axisName, chartType),
                        axisName,
                        chartType,
                      );
                      newFieldData.displayname = findNewDisplayName(
                        chartProp.properties[propKey].chartAxes,
                        newFieldData,
                        chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats,
                        newNotAllowednames,
                      );
                      newNotAllowednames.push(newFieldData.displayname);
                      return newFieldData;
                    }
                  });
                  dispatch(setMeasureAxisFields(propKey, modifiedMeasures, axisName));
                }
              }
              dispatch(callQueryAPI(propKey, true));
            }
          }
          if (showSnackBar) setShowPopup(true);
        }
      }
    },
  });

  // Properties and behaviours when another card is dropped over this card
  const [, drop] = useDrop({
    accept: ['card', 'calculation'],
    canDrop: () => false,
    collect: (monitor) => ({
      backgroundColor1: monitor.isOver({ shallow: true }) ? 1 : 0,
    }),
    hover: (item: DragItem) => {
      if (!item || !item.uId || !item.bIndex || !item.dragFrom) return;

      const dragUId = item?.uId;
      const fromBIndex = item?.bIndex;
      const dragFromOverride = item?.dragFrom?.includes('override');
      const overrideDropZone = Boolean(uID);

      // Block invalid movements
      if (dragFromOverride && !overrideDropZone) return;
      if (!dragFromOverride && overrideDropZone && item.bIndex !== 99) return;
      if (overrideDropZone && dragFromOverride && item?.overrideUId !== uID) return;

      // allow sorting when valid
      if (fromBIndex === bIndex && dragUId !== field.uId) {
        if (chartType === 'richText') {
          dispatch(sortAxesForDm(propKey, bIndex, dragUId, field.uId as string));
        } else {
          dispatch(sortAxes(propKey, bIndex, dragUId, field.uId as string, currentChartAxesName));
        }
        Logger('info', '============HOVER BLOCK END ==============');
      }
    },
  });

  useEffect(() => {
    if (chartProp.properties[propKey]?.enableOverrideForUID === '') {
      setOverrideFn(false);
    }
  }, [chartProp.properties[propKey]?.enableOverrideForUID]);

  useEffect(() => {
    //If two dimensional charts dimension, row, column, distribution without any fields, then window function will get disable
    let disableWindowFn = false;
    let _disableOverride = false;
    if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
      if (
        chartProp.properties[propKey].chartAxes[1].fields.length === 0 &&
        chartProp.properties[propKey].chartAxes[2].fields.length === 0
      ) {
        disableWindowFn = true;
        setDisable({
          ...disable,
          windowFn: true,
        });

        //while window function get disabled, it check window function having any values in redux state. If yes, then window function value will be null
        if (
          chartProp.properties[propKey].chartAxes[bIndex].fields &&
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.windowfn
        ) {
          chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].windowfn = null;
        }
      } else {
        //If two dimensional charts dimension, row, column, distribution with fields, then window function will get enable
        if (
          chartProp.properties[propKey].chartAxes[1].fields.length > 0 ||
          chartProp.properties[propKey].chartAxes[2].fields.length > 0
        ) {
          setDisable({
            ...disable,
            windowFn: false,
          });
          disableWindowFn = false;
        }
      }
    } else {
      //If one dimensional charts dimension or row without any fields, then window function will get disable
      if (!['heatmap', 'crossTab', 'boxPlot', 'richText', 'bubbleMap'].includes(chartType)) {
        if (chartProp.properties[propKey].chartAxes[1].fields.length === 0) {
          setDisable({
            ...disable,
            windowFn: true,
          });
          disableWindowFn = true;

          //while window function get disabled, it check window function having any values in redux state. If yes, then window function value will be null
          if (
            chartProp.properties[propKey].chartAxes[bIndex].fields &&
            chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.windowfn
          ) {
            chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex].windowfn = null;
          }
        } else {
          //If one dimensional charts dimension or row with fields, then window function will get enable
          if (chartProp.properties[propKey].chartAxes[1].fields.length > 0) {
            setDisable({
              ...disable,
              windowFn: false,
            });
            disableWindowFn = false;
          }
        }
      }
    }

    const calculation = field?.SavedCalculationUUID
      ? savedCalcs.find((calc) => calc.uuid === field?.SavedCalculationUUID)
      : null;
    if (calculation) {
      let disableWin = false;
      let disabledOverride = false;
      const info = calculation.calculationInfo;
      Object.values(info.flows).some((steps) =>
        steps.some((step) => {
          if (!step.flowOverride) {
            disabledOverride = false;
          } else if (
            step.flowOverride.dimension.type !== 'honorAll' ||
            step.flowOverride.filter.type !== 'honorAll'
          ) {
            disabledOverride = true;
          } else {
            disabledOverride = false;
          }
          if (step.flowOverride?.windowFunction) {
            disableWin = true;
          }

          if (disableWin && disabledOverride) return true;
          return false;
        }),
      );
      disableWindowFn = disableWin || disableWindowFn;
      _disableOverride = disabledOverride;
      /**
       * New requirement on 20-04-2026
       * If formula measure has  overrided flow or window function
       * then in chart axis(measure) we wont be able to add override and window function both
       */
      setDisable({
        windowFn: disableWindowFn || disabledOverride,
        override: disableWin || disabledOverride,
      });
      return;
    }
    setDisable({
      windowFn: disableWindowFn,
      override: _disableOverride,
    });
  }, [field, savedCalcs, chartProp.properties[propKey]?.chartAxes]);
  useEffect(() => {
    (async () => {
      // console.log(field);
      if (field?.SavedCalculationUUID) {
        const isSavedCalculation = savedCalcs.find(
          (calculation: any) =>
            calculation.uuid === field.SavedCalculationUUID &&
            calculation.datasetId === selectedDs.id,
        );
        if (isSavedCalculation) {
          let hasMissing = false;
          // console.log(isSavedCalculation);
          const fields = isSavedCalculation.calculationInfo.fields;
          for (const fieldKey of Object.keys(fields)) {
            try {
              const res = await checkIfMissingColumn(
                fields[fieldKey],
                selectedDs,
                tabTileProps.tablesForSelectedDataSets[selectedDs.id],
              );
              // console.log(fields[fieldKey]);
              // console.log(res);
              if (res.isMissing) {
                hasMissing = true;
                break;
              }
            } catch (err) {
              hasMissing = true;
              break;
            }
          }
          setIsShowWarning(hasMissing);
        } else {
          setIsShowWarning(true);
        }
        return;
      }
      const res = await checkIfMissingColumn(
        field,
        selectedDs,
        tabTileProps.tablesForSelectedDataSets[selectedDs.id],
        (field as any).originalDataSetId !== selectedDs.id,
      );
      // console.log(res);
      setIsShowWarning(
        (field as any).originalDataSetId !== selectedDs.id
          ? Boolean(!res.newTableId)
          : res.isMissing,
      );
    })();
  }, [
    chartProp.properties[propKey].selectedDs?.id,
    chartControls.properties[propKey].chartErrorDetails,
    chartProp.properties[propKey].oldSelectedDs?.id,
  ]);
  // Getting values from CardOption.tsx
  // List of options to show at the end of each card
  // (like, year, month, day, or Count, sum, avg, windowFunction etc)
  const RenderMenu = () => {
    let aggr = [];
    let timegrain = [];
    let windowfn = [];
    let overRideMenuList = [];
    let options = CardOption(axisTitle, field);
    if (options) {
      aggr = options[0];
      timegrain = options[1];
      windowfn = options[2];
      overRideMenuList = options[3];
    }

    const isItemDisabled = (optionValue: string) => {
      if (field?.isAggregated) {
        return true;
      }
      if (chartType === 'table' || chartType === 'crossTab') {
        return false;
      }
      if (chartType === 'bubbleMap') {
        const splitByAxis = chartProp.properties[propKey].chartAxes.find(
          (axis: any) => axis.name === 'Split By',
        );
        if (splitByAxis?.fields?.length > 0) {
          if (optionValue === 'Min' || optionValue === 'Max' || optionValue === 'Count Unique') {
            return true;
          }
        }
      }
      if (chartType === 'scatterPlot') {
        if (
          (axisTitle === 'X' || axisTitle === 'Y') &&
          field.timeGrain === 'date' &&
          (optionValue === 'Min' || optionValue === 'Max')
        ) {
          return true;
        }
      }
      const isDisable = checkMinMaxDisablility(optionValue);
      if (isDisable) {
        return true;
      }
      return false;
    };

    function checkMinMaxDisablility(optionValue: string) {
      //for other charts
      const measureField = chartProp.properties[propKey]?.chartAxes?.find(
        (axis: any) => axis.name === AxisType.Measure,
      );
      const isDateFieldWithMinMax = measureField?.fields.some(
        (field: any) =>
          field.timeGrain === 'date' && (optionValue === 'Min' || optionValue === 'Max'),
      );
      if (isDateFieldWithMinMax) {
        return true;
      } else {
        return false;
      }
    }
    const isDefaultAxis = ['X', 'Y', 'Measure'].includes(axisTitle);
    const shouldShowDivider =
      isDefaultAxis || (!isDefaultAxis && (aggr.length > 0 || timegrain.length > 0));
    return (
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose('clickOutside')}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        {aggr?.length > 0
          ? aggr?.map((opt: any, idx: number) => {
              const agg = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.agg;
              const isChecked = agg?.toUpperCase() === opt?.name?.toUpperCase();
              return (
                <AggregationMenuItem
                  key={opt?.id}
                  isChecked={isChecked}
                  onClick={() => handleClose('agg', opt.id)}
                  optionName={opt?.name}
                  isDisabled={opt?.name ? isItemDisabled(opt?.name) : true}
                  menuStyle={menuStyle}
                  menuSelectedStyle={menuSelectedStyle}
                />
              );
            })
          : null}
        {axisTitle === 'Dimension' ||
        axisTitle === 'Row' ||
        axisTitle === 'Column' ||
        axisTitle === 'Distribution' ||
        axisTitle === 'Split By' ||
        axisTitle === 'Date' || // to remove divider from split by bucket because their is only one rename option available
        axisTitle === 'Location' ||
        chartType === 'gauge' ||
        chartType === 'funnel' ||
        chartType === 'simplecard' ? null : (
          <Divider />
        )}
        {(chartType === 'gauge' && timegrain?.length > 0) ||
        (chartType === 'funnel' && timegrain?.length > 0) ||
        (chartType === 'simplecard' && timegrain?.length > 0) ? (
          <Divider />
        ) : null}
        {timegrain?.length > 0
          ? timegrain?.map((opt2: any, idx: number) => {
              const isChecked =
                chartProp.properties[propKey].chartAxes[bIndex].fields[
                  itemIndex
                ]?.agg?.toUpperCase() === opt2.name?.toUpperCase();
              return (
                <TimeGrainMenuItem
                  key={idx}
                  isChecked={isChecked}
                  onClick={() => handleClose('timeGrain', opt2.id)}
                  optionName={opt2.name}
                  isSelected={opt2.id === field.timeGrain}
                  menuStyle={menuStyle}
                  menuSelectedStyle={menuSelectedStyle}
                />
              );
            })
          : null}
        {axisTitle === 'Dimension' ||
        axisTitle === 'Row' ||
        axisTitle === 'Column' ||
        axisTitle === 'Distribution' ||
        axisTitle === 'Date' ||
        chartType === 'gauge' ||
        chartType === 'funnel' ||
        chartType === 'simplecard' ? null : timegrain?.length > 0 ? (
          <Divider />
        ) : null}
        {chartType === 'gauge' || chartType === 'funnel' || chartType === 'simplecard'
          ? null
          : windowfn?.length > 0
            ? [...windowfn.filter((item: any) => item?.id === 'windowfn')]?.map(
                (opt: any, idx: number) => {
                  const isChecked =
                    chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex]?.windowfn;

                  return (
                    <WindowFunctionMenuItem
                      key={opt?.id}
                      isChecked={isChecked}
                      onClick={handleWindowFunctionOnClick}
                      optionName={opt?.name}
                      isDisabled={disable.windowFn}
                    />
                  );
                },
              )
            : null}

        {chartType === 'simplecard'
          ? null
          : overRideMenuList?.length > 0
            ? overRideMenuList
                .filter((opt: any) => opt && opt.id) // Ensure only valid objects are processed
                .map((opt: any, idx: number) => {
                  const isChecked =
                    (opt.id === 'override' && field?.override) ||
                    (opt.id === 'disableFilter' && field?.disableReportFilterForOverride);
                  const isDisabled = disable.override || !!field?.hasOverridedFlow;
                  return (
                    <>
                      <Divider />
                      <IgnoreReportFilterMenuItem
                        isChecked={field?.disableReportFilterForOverride ?? false}
                        optionName={'Ignore Report filter'}
                        isDisabled={!field.override}
                        menuStyle={menuStyle}
                        onClick={(e) => {
                          let field2 = JSON.parse(JSON.stringify(field));
                          field2.disableReportFilterForOverride =
                            !field.disableReportFilterForOverride;

                          dispatch(
                            editChartPropItem('updateQuery', {
                              propKey,
                              binIndex: bIndex,
                              itemIndex,
                              item: field2,
                              currentChartAxesName,
                            }),
                          );
                          dispatch(callQueryAPI(propKey, true));

                          setAnchorEl(null);
                          setShowOptions(false);
                        }}
                      />
                      <OverrideMenuItem
                        key={idx}
                        isChecked={isChecked}
                        onClick={(e) => handleOverrideOnClick(e, opt)}
                        optionName={opt.name}
                        isDisabled={isDisabled}
                        menuStyle={{ fontSize: '12px', padding: '2px 1.5rem' }} // Pass the menu style
                      />
                    </>
                  );
                })
            : null}
        {shouldShowDivider && <Divider />}
        <MenuOptions
          uID={uID}
          windowfn={windowfn}
          axisTitle={axisTitle}
          handleClose={handleClose}
          setRenameFunction={setRenameFunction}
          setShowTooltip={setShowTooltip}
          handleRollup={handleRollup}
          menuStyle={menuStyle}
          field={field}
        />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setShowSourceDetails(true);
          }}
          sx={menuStyle}
        >
          Source Details
        </MenuItem>
      </Menu>
    );
  };
  const duplicateIndex =
    chartType === 'richText'
      ? Object.values(
          dynamicMeasureState.dynamicMeasureProps?.[dynamicMeasureState.selectedTabId]?.[
            dynamicMeasureState.selectedTileId
          ]?.[
            `${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`
          ]?.chartAxes[bIndex]?.fields || {},
        )
          .slice(0, itemIndex)
          .filter((f: any) => f.fieldname === field.fieldname).length
      : chartProp.properties[propKey]?.chartAxes?.[bIndex]?.fields
          ?.slice(0, itemIndex)
          .filter((f: any) => f.fieldname === field.fieldname)?.length || 0;

  return field ? (
    <div
      className={isShowWarning ? 'axisFieldWarning' : 'axisField'}
      ref={(node: any) => drag(drop(node))}
      style={windowFunction || overrideFn ? { border: '1.5px solid #2bb9bb' } : {}}
      onMouseOver={(event: any) => {
        setShowOptions(true);
        if (event.target.classList.contains('columnName')) setShowTooltip(true);
      }}
      onMouseLeave={() => {
        if (!open) {
          setShowOptions(false);
        }
        setShowTooltip(false);
      }}
    >
      <button
        type="button"
        className="buttonCommon columnClose"
        onClick={deleteItem}
        title="Remove field"
        style={showOptions ? { visibility: 'visible' } : { visibility: 'hidden' }}
      >
        <CloseRoundedIcon style={{ fontSize: '13px', margin: 'auto' }} />
      </button>

      <span
        className="columnName"
        style={{
          fontWeight: 'normal',
        }}
      >
        {field.displayName2 ?? field.fieldname}
      </span>

      {isShowWarning && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyItems: 'center',
            marginRight: '0.25rem',
          }}
        >
          <Tooltip
            arrow
            placement="top"
            title={'This column is missing in the dataset'}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Warning
              style={{
                color: '#850000ff',
                flexShrink: 0,
              }}
              fontSize="small"
            />
          </Tooltip>
        </div>
      )}

      {measureMappingWarning && (
        <Tooltip
          title="This measure is not mapped. Map it to have an effect of Measure Level Control. Go to Chart controls -> User Level Filter -> Measure Level Control -> Then choose from Measure Mapping drop down."
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingRight: '5px',
            }}
          >
            <WarningIcon
              style={{
                cursor: 'pointer',
                color: 'orange',
                fontSize: '1.5em',
                transition: 'color 0.3s, font-size 0.3s',
              }}
            ></WarningIcon>
          </span>
        </Tooltip>
      )}

      {/* window function have any values in state, then window icon will get enable */}
      {chartType === 'gauge' ||
      chartType === 'funnel' ||
      chartType === 'simplecard' ? null : chartProp.properties[propKey].chartAxes[bIndex].fields[
          itemIndex
        ]?.windowfn ? (
        <WindowFunctionButton onClick={handleWindowFunctionOnClick} isEnabled={true} />
      ) : null}
      {chartType === 'simplecard' ? null : chartProp.properties[propKey].chartAxes[bIndex].fields[
          itemIndex
        ]?.override ? (
        <OverrideFunction onClick={(e) => handleOverrideOnClick(e, { id: 'override' })} />
      ) : null}
      {chartType === 'simplecard' ? null : chartProp.properties[propKey][currentChartAxesName][
          bIndex
        ].fields[itemIndex]?.rollupDepth ? (
        <RollUpButton onClick={handleRollup} />
      ) : null}

      <AggregationDisplay
        isAggregated={field.isAggregated || false}
        agg={field.agg}
        timeGrain={field.timeGrain}
        prefix={field.prefix}
      />

      {/* {field.isAggregated ? (
        <div style={{ marginRight: '4px' }}></div>
      ) : ( */}

      <button
        type="button"
        className="buttonCommon columnDown"
        // Get the index among fields with the same fieldname
        data-testid={`${axisTitle.replace(/\s+/g, '_')}_${field.fieldname}_${duplicateIndex}`}
        onClick={(e: any) => {
          handleClick(e);
        }}
        title="Click for Menu Options"
        style={
          showOptions
            ? { visibility: 'visible', paddingLeft: '1px' }
            : { visibility: 'hidden', paddingLeft: '1px' }
        }
      >
        <KeyboardArrowDownRoundedIcon style={{ fontSize: '14px', margin: 'auto' }} />
      </button>
      {/* )} */}

      <RenderMenu />
      {windowFunction ? (
        <WindowFunction
          anchorElm={anchorElment}
          haswindowfn={windowFunction}
          setWindowfn={setWindowFunction}
          propKey={propKey}
          bIndex={bIndex}
          itemIndex={itemIndex}
        />
      ) : null}
      <RenameFunction
        renamefn={renameFunction}
        setRenamefn={setRenameFunction}
        propKey={propKey}
        bIndex={bIndex}
        itemIndex={itemIndex}
      />
      {showSourceDetails && (
        <SourceDetails field={field} open={showSourceDetails} setOpen={setShowSourceDetails} />
      )}
      <CustomTooltip
        title={field.displayname}
        arrow
        open={showTooltip}
        isShowWarning={isShowWarning}
        placement="right-end"
        style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '8px',
          fontSize: '12px',
        }}
      >
        <span style={{ display: 'inline-block', fontSize: 0, lineHeight: 0 }}></span>
      </CustomTooltip>
      <SnackBar
        show={showPopup}
        message={messages.playbook.WindowFunctionRemoved}
        onClose={() => setShowPopup(false)}
      />
    </div>
  ) : null;
};

export default Card;
